/**
 * Copyright Mántum All Rights Reserved.
 */
import debug from 'debug';
import Input from '../../form/input';
import Select from '../../form/select';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import type { Option, Detail, Column } from '@mantum/kanela';

const info = debug('kanela:datatable/simple');

export type Value = {
    name: string;
    value: () => Record<string, Detail<unknown>>;
    clear: () => void;
};

type Limit = {
    min?: number;
    max?: number;
};

type PropTypes = {
    columns?: (Column & Limit)[];
    name: string;
    onChange?: (name: string, value: Record<string, unknown>) => void;
    value?: Record<string, unknown>;
};

export default forwardRef<Value, PropTypes>(function Row(props, ref) {
    const valuesRef = useRef<Record<string, unknown>>({});
    const rowRef = useRef<Record<string, Detail<unknown>>>({});

    useImperativeHandle(
        ref,
        () => {
            return {
                name: props.name,
                clear() {
                    for (const [key, value] of Object.entries(rowRef.current)) {
                        info('clear field: %s', key);
                        value.clear();
                    }
                },
                value() {
                    return rowRef.current;
                }
            };
        },
        [props.name]
    );

    function subscribe(ref: Detail<unknown>) {
        if (ref && ref.name) {
            rowRef.current[ref.name] = ref;
        }
    }

    function onChange(name?: string, value?: unknown) {
        if (name) {
            valuesRef.current[name] = value;
        }

        if (props.onChange) {
            props.onChange(props.name, valuesRef.current);
        }
    }

    return (
        <tr>
            {props.columns?.map((it, index) => (
                <td
                    className='min-w-36 border h-10'
                    data-row={index}
                    key={it.name}
                >
                    {it.type === 'number' && (
                        <Input
                            disabled={it.readonly}
                            type='number'
                            ref={subscribe}
                            onChange={onChange}
                            min={it.min}
                            max={it.max}
                            name={it.name}
                            value={
                                !isNaN(Number(props.value?.[it.name]))
                                    ? Number(props.value?.[it.name])
                                    : undefined
                            }
                        />
                    )}
                    {it.type === 'select' && (
                        <Select
                            disabled={it.readonly}
                            name={it.name}
                            ref={subscribe}
                            onChange={onChange}
                            value={props.value?.[it.name] as Option<unknown>}
                        />
                    )}
                </td>
            ))}
        </tr>
    );
});
